import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useMyFees } from '../../../hooks/useFees';
import { FeePaymentModal } from './FeePaymentModal';

const PAYER_ROLES = new Set(['student', 'parent']);

/** One key per sign-in, so "Remind me later" lasts until the next login. */
function dismissKey(userId) {
  let tokenTail = '';
  try {
    tokenTail = String(window.localStorage.getItem('auth-token') || '').slice(-16);
  } catch {
    // Storage blocked: fall back to per-user key.
  }
  return `fee-prompt-dismissed:${userId}:${tokenTail}`;
}

function readDismissed(userId) {
  try {
    return window.sessionStorage.getItem(dismissKey(userId)) === '1';
  } catch {
    return false;
  }
}

/**
 * Pops up for students/parents with an unpaid fee each time they sign in.
 * Once shown it stays open (e.g. on the "submitted" screen) until closed.
 */
export function FeeDuePrompt() {
  const { user } = useAuth();
  const isPayer = !!user && PAYER_ROLES.has(user.role) && !user.mustChangePassword;
  const { fees, settings, loading } = useMyFees({ enabled: isPayer });
  const [dismissed, setDismissed] = useState(() => (user ? readDismissed(user.id) : false));
  const [shown, setShown] = useState(false);

  const hasPending = useMemo(() => fees.some((f) => f.status === 'pending'), [fees]);

  useEffect(() => {
    if (isPayer && !loading && settings?.enabled && hasPending && !dismissed) setShown(true);
  }, [isPayer, loading, settings?.enabled, hasPending, dismissed]);

  const close = () => {
    setDismissed(true);
    setShown(false);
    try {
      window.sessionStorage.setItem(dismissKey(user.id), '1');
    } catch {
      // Ignore; the prompt just won't be remembered across reloads.
    }
  };

  if (!isPayer) return null;
  return (
    <FeePaymentModal
      open={shown && !dismissed}
      onClose={close}
      fees={fees}
      settings={settings}
      showStudent={user.role === 'parent'}
      dismissLabel={hasPending ? 'Remind me later' : 'Close'}
    />
  );
}
